import * as React from 'react';
import { useSelector } from 'react-redux';
import { IConfig } from '@cian/config/shared';
import { NewbuildingChatDesktop, prepareUserData } from '@cian/newbuilding-chat-component';

import { isNewbuildingSelector, isInRegionSelector, isFromBuilderSelector } from '../../selectors/offerData/offer';
import { getWebimUserDataSelector } from '../../selectors/getWebimUserData';
import { isNeedToShow, updateShownTimestamp } from '../../utils/newbuildingChat/periodicActions';
import { CHAT_OPEN_DELAY, CHAT_FORCE_OPEN_KEY, CHAT_FORCE_OPEN_INTERVAL } from '../../constants/newbuildingChat';

interface INewbuildingChatContainerProps {
  config: IConfig;
}

export function NewbuildingChatContainer({ config }: INewbuildingChatContainerProps) {
  const [isOpen, toggleOpen] = React.useState(false);
  const [needForceOpen, setNeedForceOpen] = React.useState(false);

  const onOpenedChange = React.useCallback(
    (isOpen: boolean) => {
      toggleOpen(isOpen);

      if (isOpen) {
        setNeedForceOpen(false);
      }
    },
    [needForceOpen],
  );

  const chatEnabled = config.get<boolean>('webim.chat.Enabled');
  const accountName = config.get<string>('webim.accountName');
  const needForceOpenRS = config.get<boolean>('newbuildingChat.needForceOpen');
  const chatEnabledRegions = config.get<number[]>('newbuildingChat.enabledRegions') || [];

  const isNewbuilding = useSelector(isNewbuildingSelector);
  const availableInRegion = useSelector(isInRegionSelector(chatEnabledRegions));
  const isFromBuilder = useSelector(isFromBuilderSelector);
  const userData = useSelector(getWebimUserDataSelector);

  const availableToShow = chatEnabled && accountName && isNewbuilding && availableInRegion && isFromBuilder;

  const onReady = React.useCallback(() => {
    if (availableToShow && needForceOpen) {
      setTimeout(() => {
        onOpenedChange(true);
        updateShownTimestamp(CHAT_FORCE_OPEN_KEY, CHAT_FORCE_OPEN_INTERVAL);
      }, CHAT_OPEN_DELAY);
    }
  }, [availableToShow, needForceOpen]);

  React.useEffect(() => {
    if (needForceOpenRS && isNeedToShow(CHAT_FORCE_OPEN_KEY)) {
      setNeedForceOpen(true);
    }
  }, []);

  if (!availableToShow) {
    return null;
  }

  return (
    <NewbuildingChatDesktop
      accountName={accountName as string}
      visible
      tooltipVisible={!isOpen}
      isOpen={isOpen}
      userData={prepareUserData(userData)}
      toggleOpen={onOpenedChange}
      onReady={onReady}
    />
  );
}
